import isOnLocalhost from '@client-shared/utils/is-on-localhost'
import { createHead, VueHeadMixin } from '@unhead/vue/client'
import { createApp } from 'vue'
import VueDraggable from 'vuedraggable'

import App from '@/App.vue'
import PfIcon from '@/components/PfIcon.vue'
import analytics from '@/plugins/analytics.js'
import auth from '@/plugins/auth'
import axiosPlugin from '@/plugins/axios'
import broadcastChannel from '@/plugins/broadcast-channel.js'
import chunkReload from '@/plugins/chunk-reload.js'
import clipboardUppy from '@/plugins/clipboard-uppy.js'
import configInject from '@/plugins/config-inject'
import dayjs from '@/plugins/dayjs'
import enableTextSelection from '@/plugins/directives/enable-text-selection'
import i18n from '@/plugins/i18n'
import localStorageListener from '@/plugins/localstorage-listener.js'
import localStorageMigrations from '@/plugins/localstorage-migrations.js'
import logger from '@/plugins/logger.js'
import migrateLegacyClipboardFiles from '@/plugins/migrate-legacy-clipboard-files.js'
import raygun from '@/plugins/raygun.js'
import SafeHtmlDirective from '@/plugins/safe-html'
import scrollToError from '@/plugins/scroll-to-error'
import scrollToTop from '@/plugins/scroll-to-top'
import socketIo from '@/plugins/socket-io.js'
import unsavedChanges from '@/plugins/unsaved-changes.js'
import utm from '@/plugins/utm.js'
import wait from '@/plugins/wait'
import router from '@/router'
import $store from '@/store/index.js'

import 'reset-css/reset.css'
import '@/assets/scss/web_main.scss'

import 'swiper/css'
import 'swiper/css/thumbs'
import 'swiper/css/navigation'

const app = createApp(App)

// enable to debug performance issues with vue developer tools
// app.config.performance = true

const head = createHead()

app
  .use(SafeHtmlDirective)
  .use(configInject)
  .use($store)
  .use(i18n)
  .use(router)
  .use(localStorageMigrations)
  .use(axiosPlugin)
  .use(auth)
  .use(migrateLegacyClipboardFiles)
  .use(logger)
  .use(wait)
  .use(utm)
  .use(dayjs)
  .use(clipboardUppy)
  .use(broadcastChannel)
  .use(enableTextSelection)
  .use(analytics)
  .use(scrollToError)
  .use(scrollToTop)
  .use(localStorageListener)
  .use(unsavedChanges)
  // socketIo registers a router beforeEach guard that connects to the project
  // room. It must install AFTER unsavedChanges so the unsaved-changes guard
  // (which can abort navigation by returning false) runs first — otherwise the
  // socket would switch rooms for a navigation that ends up cancelled.
  .use(socketIo)
  .use(head)
  .use(raygun)
  .use(chunkReload)
  .mixin(VueHeadMixin) // make head function in pages work like in nuxt 2
  .component('PfIcon', PfIcon)
  .component('VueDraggable', VueDraggable)

// We expose the app instance to the window object ONLY IN CYPRESS, so we can override the config
// TODO @franzgollhammer verify
if (window.Cypress) {
  window.__pf_cypress_vue_instance_config__ = app.config.globalProperties.$config
  window.__pf_cypress_router__ = router
  // Lets E2E tests simulate a PRINT_JOB_UPDATE socket event by committing the
  // finished print job directly (mirrors ionsrc/main.js).
  window.__pf_cypress_vue_instance_$store__ = $store
  // The logger, so a spec can check that entries reach the storage and the server (mirrors ionsrc/main.js).
  window.__pf_cypress_vue_instance_$logger__ = app.config.globalProperties.$logger
}

// https://stackoverflow.com/questions/41060472/how-to-disable-the-context-menu-on-long-press-when-using-device-mode-in-chrome
if (isOnLocalhost) {
  console.log('Running in development mode. Disabled long press to open native browser context menu')

  window.oncontextmenu = function (event) {
    const pointerEvent = event

    if (pointerEvent.pointerType === 'touch') {
      // context menu was triggered by long press
      return false
    }

    // just to show that pointerEvent.pointerType has another value 'mouse' aka right click
    if (pointerEvent.pointerType === 'mouse') {
      // context menu was triggered by right click
      return true
    }

    // returning true will show a context menu for other cases
    return true
  }
}

app.mount('#app')
